import React from "react";
import styled from "styled-components";
import { Box, Flex } from "rebass/styled-components";

import { useSteambot } from "MVVM/Hooks/useSteambot";
import { useClipSettingsModal } from "MVVM/Hooks/useClipQualitySettingsModal";
import { analytics } from "MVVM/Analytics";

// Assignment is not a reservation - it only decides which bot to show - so we
// ask for one as soon as this view opens (from the bubble's modal or the
// settings tab) instead of making the user press another button.

// The enrollment flow, shared by the bottom-right bubble modal and the
// account-settings tab: request a bot -> add it on Steam -> poll until the
// friendship lands -> success.
export const SteambotEnroll = () => {
  const { status, enrolled, assignedBot, assigning, requestAssignment } =
    useSteambot();
  const { open: openClipSettings } = useClipSettingsModal();
  const requested = React.useRef(false);

  React.useEffect(() => {
    if (requested.current) return;
    if (
      !status?.eligible ||
      enrolled ||
      !status?.capacityAvailable ||
      assignedBot
    )
      return;
    requested.current = true;
    analytics.track("Steambot - Request Assignment", {});
    requestAssignment();
  }, [
    status?.eligible,
    status?.capacityAvailable,
    enrolled,
    assignedBot,
    requestAssignment,
  ]);

  if (!status?.eligible) {
    return (
      <Muted>
        Steam bot match tracking is not available for your account yet.
      </Muted>
    );
  }

  if (enrolled) {
    return (
      <Flex flexDirection="column" alignItems="center" style={{ gap: 12 }}>
        <SuccessMark>✓</SuccessMark>
        <Success>You&apos;re connected!</Success>
        <Muted>
          Your Premier, Competitive and Wingman matches are now detected the
          moment they end, and your AutoCapture clips arrive as Steam messages.
        </Muted>
        <AutoCaptureNote openClipSettings={openClipSettings} />
      </Flex>
    );
  }

  if (assignedBot) {
    return (
      <Flex flexDirection="column" style={{ gap: 12 }}>
        <StepList>
          <li>
            Open{" "}
            <BotLink
              href={assignedBot.profileUrl}
              target="_blank"
              rel="noreferrer"
            >
              your Allstar bot&apos;s Steam profile
            </BotLink>
          </li>
          <li>Click &quot;Add Friend&quot;</li>
        </StepList>
        <Muted>Waiting for your friend request…</Muted>
        <AutoCaptureNote openClipSettings={openClipSettings} />
      </Flex>
    );
  }

  if (!status?.capacityAvailable) {
    return (
      <Muted>
        All Allstar Steam bots are currently full - check back soon.
      </Muted>
    );
  }

  // the effect above is fetching the bot; the button is the retry path if it
  // failed
  return (
    <Flex flexDirection="column" alignItems="flex-start" style={{ gap: 12 }}>
      <Muted>
        Add the Allstar bot as a Steam friend and we&apos;ll detect the moment
        your Premier, Competitive or Wingman match ends - your match gets
        processed right away and your AutoCapture clip links arrive over Steam
        chat.
      </Muted>
      <ConnectButton disabled={assigning} onClick={() => requestAssignment()}>
        {assigning ? "Finding your bot…" : "Connect the Steam bot"}
      </ConnectButton>
      <AutoCaptureNote openClipSettings={openClipSettings} />
    </Flex>
  );
};

// Clips only arrive over Steam chat if AutoCapture is on, which lives in the
// clipping settings modal.
const AutoCaptureNote = ({
  openClipSettings,
}: {
  openClipSettings: () => void;
}) => (
  <Muted>
    Get your AutoCapture clips quickly in Steam messages — turn AutoCapture on
    in{" "}
    <SettingsLink
      onClick={() => {
        analytics.track("Steambot - Open Clip Settings", {});
        openClipSettings();
      }}
    >
      Clipping settings
    </SettingsLink>
    .
  </Muted>
);

const SettingsLink = styled.span`
  color: ${({ theme }) => theme.colors.envy};
  cursor: pointer;
  text-decoration: underline;
`;

const Muted = styled(Box)`
  color: ${({ theme }) => theme.colors.chalk};
  font-size: 14px;
  line-height: 1.5;
`;

const Success = styled(Box)`
  color: ${({ theme }) => theme.colors.envy};
  font-family: "Barlow", sans-serif;
  font-size: 18px;
  font-weight: 700;
`;

const SuccessMark = styled(Flex)`
  align-items: center;
  background: ${({ theme }) =>
    theme.colors.envyAlpha20 || "rgba(0, 255, 163, 0.15)"};
  border-radius: 50%;
  color: ${({ theme }) => theme.colors.envy};
  font-size: 22px;
  height: 44px;
  justify-content: center;
  width: 44px;
`;

const BotLink = styled.a`
  color: ${({ theme }) => theme.colors.envy};
  text-decoration: underline;
`;

const StepList = styled.ol`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 14px;
  line-height: 1.8;
  margin: 0;
  padding-left: 20px;
`;

const ConnectButton = styled.button`
  background: ${({ theme }) => theme.colors.envy};
  border: none;
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.navy};
  cursor: pointer;
  font-family: "Barlow", sans-serif;
  font-size: 14px;
  font-weight: 700;
  padding: 10px 18px;

  &:disabled {
    cursor: default;
    opacity: 0.6;
  }
`;
